import React, { useState, useEffect, useMemo } from 'react';
import {
  getActivityDashboardMetrics,
  formatUsageDuration,
  type ActivityDashboardMetrics,
  type ActivityActionType,
  type DateWiseMetricSummary,
} from '../../lib/activityLogger';
import { supabase } from '../../lib/supabase';
import { sha256Hex } from '../../lib/secureHash';
import { generateQuotationPDF } from '../../lib/pdfGenerator';
import { generateQuotationExcel } from '../../lib/excelGenerator';
import StaffControl from './StaffControl';
import type { Quotation, Party, Company } from '../../types';

// Super Admin portal credentials.
// The master key is stored as a salted SHA-256 hash — the plaintext is never
// present in the JS bundle. To rotate the key, update this hash:
//   sha256Hex('ski_audit_vault_v1:' + NEW_KEY)
const SUPER_ADMIN_IDENTIFIERS = [
  'superadmin@skindustries.internal',
  'ski_master_superadmin',
  'superadmin',
];
const VAULT_KEY_HASH_SALT = 'ski_audit_vault_v1';
const VAULT_KEY_HASH = '85c5fc187a37a1523542514f61e0027f1ac057c6e3048c2a728f4205043e42f2';
const SESSION_TOKEN_KEY = 'ski_stealth_super_admin_session_v1';
const ATTEMPTS_KEY = 'ski_vault_failed_attempts_v1';

type DatePreset = 'all' | 'today' | 'yesterday' | '7days' | '30days' | 'custom';
type TabType = 'timeline' | 'users' | 'pdfs' | 'excels' | 'stream' | 'staff';

export default function SecretAuditPortal() {
  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(SESSION_TOKEN_KEY) === 'CLEARANCE_GRANTED_LEVEL_5';
  });

  const [authId, setAuthId] = useState('');
  const [authPass, setAuthPass] = useState('');
  const [authError, setAuthError] = useState('');
  // Failed-attempt counter persists in localStorage so a page reload cannot
  // reset the lockout (the previous in-memory counter could be bypassed by F5).
  const [authAttempts, setAuthAttempts] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    const raw = window.localStorage.getItem(ATTEMPTS_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  const [showPassword, setShowPassword] = useState(false);

  // Dashboard Data State
  const [metrics, setMetrics] = useState<ActivityDashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('timeline');

  // Filters State
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserFilter, setSelectedUserFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');

  // Sub-tabs for Excel Management
  const [excelSubTab, setExcelSubTab] = useState<'downloads' | 'uploads'>('downloads');

  // Action status state
  const [generatingDoc, setGeneratingDoc] = useState<string | null>(null);

  // Load metrics when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadAuditData();
    }
  }, [isAuthenticated]);

  async function loadAuditData() {
    setLoading(true);
    try {
      const data = await getActivityDashboardMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('Failed to load activity metrics:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (authAttempts >= 5) {
      setAuthError('Security Protocol: Terminal locked due to repeated invalid credentials.');
      return;
    }

    const cleanId = authId.trim().toLowerCase();
    const isIdValid = SUPER_ADMIN_IDENTIFIERS.includes(cleanId);
    // Compare hashed input against the stored hash (plaintext key is never bundled)
    const isPassValid = sha256Hex(VAULT_KEY_HASH_SALT + ':' + authPass) === VAULT_KEY_HASH;

    if (isIdValid && isPassValid) {
      sessionStorage.setItem(SESSION_TOKEN_KEY, 'CLEARANCE_GRANTED_LEVEL_5');
      window.localStorage.removeItem(ATTEMPTS_KEY);
      setIsAuthenticated(true);
      setAuthError('');
    } else {
      const next = authAttempts + 1;
      setAuthAttempts(next);
      try {
        window.localStorage.setItem(ATTEMPTS_KEY, String(next));
      } catch {
        // storage unavailable — in-memory counter still applies
      }
      setAuthError('Access Denied: Invalid Super Admin Identifier or Master Key.');
    }
  }

  function handleLogout() {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    setIsAuthenticated(false);
    setAuthId('');
    setAuthPass('');
  }

  // Date Range Calculator
  const dateRangeBounds = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];

    if (datePreset === 'today') {
      return { start: todayStr, end: todayStr };
    }
    if (datePreset === 'yesterday') {
      const y = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const yStr = y.toISOString().split('T')[0];
      return { start: yStr, end: yStr };
    }
    if (datePreset === '7days') {
      const d7 = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { start: d7.toISOString().split('T')[0], end: todayStr };
    }
    if (datePreset === '30days') {
      const d30 = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { start: d30.toISOString().split('T')[0], end: todayStr };
    }
    if (datePreset === 'custom') {
      return {
        start: customStartDate || '1970-01-01',
        end: customEndDate || '2099-12-31',
      };
    }
    return null;
  }, [datePreset, customStartDate, customEndDate]);

  // Filtered Logs based on selected scope
  const filteredLogs = useMemo(() => {
    if (!metrics?.recentLogs) return [];

    return metrics.recentLogs.filter((log) => {
      // 1. Date Scope Check
      if (dateRangeBounds) {
        const logDate = (log.created_at || '').split('T')[0];
        if (logDate < dateRangeBounds.start || logDate > dateRangeBounds.end) {
          return false;
        }
      }

      // 2. User Filter Check
      if (selectedUserFilter !== 'all' && log.email.toLowerCase() !== selectedUserFilter.toLowerCase()) {
        return false;
      }

      // 3. Action Filter Check
      if (actionFilter !== 'all' && log.action_type !== actionFilter) {
        return false;
      }

      // 4. Search Query Check
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesResource = (log.resource_id || '').toLowerCase().includes(q);
        const matchesUser = (log.user_name || '').toLowerCase().includes(q);
        const matchesEmail = log.email.toLowerCase().includes(q);
        const matchesDetails = (log.details || '').toLowerCase().includes(q);
        const matchesIp = (log.ip_address || '').toLowerCase().includes(q);
        if (!matchesResource && !matchesUser && !matchesEmail && !matchesDetails && !matchesIp) {
          return false;
        }
      }

      return true;
    });
  }, [metrics, dateRangeBounds, selectedUserFilter, actionFilter, searchQuery]);

  // Date-wise timeline aggregator
  const filteredDateTimeline: DateWiseMetricSummary[] = useMemo(() => {
    if (!metrics?.dateMetrics) return [];
    if (!dateRangeBounds) return metrics.dateMetrics;

    return metrics.dateMetrics.filter(
      (d: DateWiseMetricSummary) => d.date >= dateRangeBounds.start && d.date <= dateRangeBounds.end
    );
  }, [metrics, dateRangeBounds]);

  // Dynamic counts for current scope
  const rangeCounts = useMemo(() => {
    let quotes = 0;
    let pdfs = 0;
    let excels = 0;
    let uploads = 0;
    const users = new Set<string>();

    filteredLogs.forEach((l) => {
      if (l.email) users.add(l.email);
      if (l.action_type === 'QUOTATION_CREATED') quotes++;
      if (l.action_type === 'PDF_GENERATED') pdfs++;
      if (l.action_type === 'EXCEL_GENERATED' || l.action_type === 'REPORT_EXPORTED') excels++;
      if (l.action_type === 'EXCEL_UPLOADED') uploads++;
    });

    if (datePreset === 'all' && selectedUserFilter === 'all' && !searchQuery && metrics) {
      return {
        quotes: metrics.totalQuotes,
        pdfs: metrics.totalPdfs,
        excels: metrics.totalExcels,
        uploads: metrics.totalUploads,
        usersCount: metrics.userMetrics.length,
      };
    }

    return {
      quotes,
      pdfs,
      excels,
      uploads,
      usersCount: users.size,
    };
  }, [filteredLogs, datePreset, selectedUserFilter, searchQuery, metrics]);

  // Re-generate and Download Quotation PDF directly from vault
  async function handleDownloadQuotePdf(quoteNum: string) {
    if (!quoteNum || generatingDoc) return;
    setGeneratingDoc(quoteNum);
    try {
      const { data: qData } = await supabase
        .from('quotations')
        .select('*')
        .eq('quotation_number', quoteNum)
        .single();

      if (!qData) {
        alert(`Quotation ${quoteNum} not found in database records.`);
        return;
      }

      const [cRes, pRes, itRes] = await Promise.all([
        supabase.from('companies').select('*').eq('id', qData.company_id).single(),
        supabase.from('parties').select('*').eq('id', qData.party_id).single(),
        supabase.from('quotation_items').select('*').eq('quotation_id', qData.id).order('sno'),
      ]);

      const company: Company = cRes.data || { id: 1, name: 'Sai Krishna Industries', code: 'SKI', address: '', phone: '', email: '' };
      const party: Party = pRes.data || { id: 1, name: 'Client Party', address1: '' };
      const items = itRes.data || [];

      const doc = generateQuotationPDF(qData as Quotation, company, party, items);
      doc.save(`${quoteNum.replace(/[^a-zA-Z0-9]/g, '_')}_Vault.pdf`);
    } catch (e: any) {
      alert('Error regenerating PDF: ' + (e?.message || 'Check database connection'));
    } finally {
      setGeneratingDoc(null);
    }
  }

  // Re-generate and Download Quotation Excel directly from vault
  async function handleDownloadQuoteExcel(quoteNum: string) {
    if (!quoteNum || generatingDoc) return;
    setGeneratingDoc(quoteNum);
    try {
      const { data: qData } = await supabase
        .from('quotations')
        .select('*')
        .eq('quotation_number', quoteNum)
        .single();

      if (!qData) {
        alert(`Quotation ${quoteNum} not found in database records.`);
        return;
      }

      const [cRes, pRes, itRes] = await Promise.all([
        supabase.from('companies').select('*').eq('id', qData.company_id).single(),
        supabase.from('parties').select('*').eq('id', qData.party_id).single(),
        supabase.from('quotation_items').select('*').eq('quotation_id', qData.id).order('sno'),
      ]);

      const company: Company = cRes.data || { id: 1, name: 'Sai Krishna Industries', code: 'SKI', address: '', phone: '', email: '' };
      const party: Party = pRes.data || { id: 1, name: 'Client Party', address1: '' };
      const items = itRes.data || [];

      await generateQuotationExcel(qData as Quotation, company, party, items);
    } catch (e: any) {
      alert('Error regenerating Excel: ' + (e?.message || 'Check database connection'));
    } finally {
      setGeneratingDoc(null);
    }
  }

  // Export filtered logs to CSV
  function exportLogsCSV() {
    if (filteredLogs.length === 0) {
      alert('No activity logs available for export with current filters.');
      return;
    }

    const headers = [
      'Timestamp',
      'Date',
      'Action Type',
      'User Name',
      'Email',
      'Role',
      'Resource / Target',
      'Details',
      'IP Address',
      'Device',
      'Browser',
    ];

    const rows = filteredLogs.map((l) => [
      `"${l.created_at}"`,
      `"${l.created_at ? l.created_at.split('T')[0] : ''}"`,
      `"${l.action_type}"`,
      `"${l.user_name || ''}"`,
      `"${l.email}"`,
      `"${l.role || ''}"`,
      `"${l.resource_id || ''}"`,
      `"${(l.details || '').replace(/"/g, '""')}"`,
      `"${l.ip_address || ''}"`,
      `"${l.device_type || ''}"`,
      `"${l.browser || ''}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SKI_Activity_Ledger_${datePreset}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── Render Authentication Screen (Clean White Theme) ───
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col justify-center items-center p-4 font-sans select-none">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 shadow-xl relative overflow-hidden">
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-emerald-50 border border-emerald-200 rounded-2xl mx-auto flex items-center justify-center text-3xl mb-4 shadow-sm text-emerald-600">
              🛡️
            </div>
            <div className="inline-block px-3 py-1 rounded-full text-[10px] tracking-widest uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold mb-2">
              EXECUTIVE AUDIT VAULT
            </div>
            <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Super Admin Command Center</h1>
            <p className="text-xs text-slate-500 mt-1">
              Classified Activity, Usage Times & Generation Ledger
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Super Admin Identifier
              </label>
              <input
                type="text"
                required
                value={authId}
                onChange={(e) => setAuthId(e.target.value)}
                placeholder="superadmin or internal handle"
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono transition-all"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Master Security Key
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  {showPassword ? 'Hide Key' : 'Reveal Key'}
                </button>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={authPass}
                onChange={(e) => setAuthPass(e.target.value)}
                placeholder="••••••••••••••••"
                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono transition-all"
              />
            </div>

            {authError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-medium flex items-center gap-2">
                <span>⚠️</span>
                <span>{authError}</span>
              </div>
            )}

            <button
              type="submit"
              className="w-full mt-2 py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs tracking-wider uppercase rounded-xl shadow-md shadow-emerald-600/20 transition-all active:scale-[0.98] cursor-pointer"
            >
              Verify Clearance & Unlock Vault
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-100 text-center">
            <span className="text-[11px] text-slate-400 font-medium">
              Stealth Encrypted Ledger · Sai Krishna Industries
            </span>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render Main Super Admin Command Center (White Theme) ───
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col font-sans">
      {/* Top Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-xl text-emerald-600 font-bold shadow-sm">
              ⚡
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-slate-900 text-sm tracking-wide">
                  SAI KRISHNA AUDIT VAULT
                </span>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  SUPER ADMIN
                </span>
              </div>
              <p className="text-[11px] text-slate-500">Master Activity, Usage Times & Export Ledger</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={loadAuditData}
              disabled={loading}
              className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-300 flex items-center gap-1.5 transition-colors disabled:opacity-50 cursor-pointer font-medium"
            >
              <span className={loading ? 'animate-spin' : ''}>🔄</span>
              <span>Refresh</span>
            </button>

            <button
              onClick={exportLogsCSV}
              className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <span>📥</span>
              <span>Export CSV</span>
            </button>

            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-xs bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer font-medium"
            >
              <span>🔒</span>
              <span>Lock Terminal</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 w-full space-y-6">
        {/* Global Date Filter Bar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              📅 Date Scope:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  { id: 'all', label: 'All Time' },
                  { id: 'today', label: 'Today' },
                  { id: 'yesterday', label: 'Yesterday' },
                  { id: '7days', label: 'Last 7 Days' },
                  { id: '30days', label: 'Last 30 Days' },
                  { id: 'custom', label: 'Custom Range' },
                ] as const
              ).map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => setDatePreset(preset.id)}
                  className={`px-3 py-1 text-xs rounded-lg font-semibold transition-all cursor-pointer ${
                    datePreset === preset.id
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {datePreset === 'custom' && (
            <div className="flex items-center gap-2 pt-2 md:pt-0">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs text-slate-800 focus:outline-none focus:border-emerald-500 font-mono"
              />
              <span className="text-xs text-slate-400">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs text-slate-800 focus:outline-none focus:border-emerald-500 font-mono"
              />
            </div>
          )}

          <div className="text-xs font-medium text-slate-500">
            Active Scope: <strong className="text-emerald-700 font-bold">{filteredLogs.length} Events</strong>
          </div>
        </div>

        {/* Dynamic 5 KPI Cards (White Cards with subtle shadows) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
          {/* Card 1: Quotations Created */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Quotes Created
              </span>
              <span className="p-1.5 rounded-lg bg-blue-50 text-blue-600 text-base border border-blue-100">
                📋
              </span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold font-mono text-slate-900">{rangeCounts.quotes}</span>
              <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                Created
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Quotations generated in range</p>
          </div>

          {/* Card 2: PDFs Generated */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                PDFs Generated
              </span>
              <span className="p-1.5 rounded-lg bg-purple-50 text-purple-600 text-base border border-purple-100">
                📑
              </span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold font-mono text-slate-900">{rangeCounts.pdfs}</span>
              <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200">
                Downloaded
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Quotation PDFs exported</p>
          </div>

          {/* Card 3: Excels Generated */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Excels Generated
              </span>
              <span className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-base border border-emerald-100">
                📊
              </span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold font-mono text-slate-900">{rangeCounts.excels}</span>
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                Exported
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Quotes & reports exported</p>
          </div>

          {/* Card 4: Excel Uploads & Imports */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Excel Uploads
              </span>
              <span className="p-1.5 rounded-lg bg-amber-50 text-amber-600 text-base border border-amber-100">
                📤
              </span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold font-mono text-slate-900">{rangeCounts.uploads}</span>
              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                Uploaded
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Bulk master data imports</p>
          </div>

          {/* Card 5: Total Usage Time */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm relative overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Total Usage Time
              </span>
              <span className="p-1.5 rounded-lg bg-cyan-50 text-cyan-600 text-base border border-cyan-100">
                ⏱️
              </span>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold font-mono text-slate-900">
                {formatUsageDuration(metrics?.totalUsageMinutes || 0)}
              </span>
              <span className="text-[10px] font-bold text-cyan-700 bg-cyan-50 px-1.5 py-0.5 rounded border border-cyan-200">
                {rangeCounts.usersCount} Staff
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Calculated active engagement</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-2 pt-2 rounded-t-xl shadow-sm">
          <nav className="flex space-x-2 overflow-x-auto pb-1">
            <button
              onClick={() => setActiveTab('timeline')}
              className={`pb-3 px-3.5 text-xs font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'timeline'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>📅</span>
              <span>Date-Wise Activity Timeline</span>
              <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full font-semibold">
                {filteredDateTimeline.length} Days
              </span>
            </button>

            <button
              onClick={() => setActiveTab('users')}
              className={`pb-3 px-3.5 text-xs font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'users'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>⏱️</span>
              <span>Usage Times & Productivity</span>
              <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full font-semibold">
                {metrics?.userMetrics.length || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('pdfs')}
              className={`pb-3 px-3.5 text-xs font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'pdfs'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>📑</span>
              <span>PDF Generation Ledger</span>
              <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full font-semibold">
                {rangeCounts.pdfs}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('excels')}
              className={`pb-3 px-3.5 text-xs font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'excels'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>📊</span>
              <span>Excel Operations (Upload / Download)</span>
              <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full font-semibold">
                {rangeCounts.excels + rangeCounts.uploads}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('stream')}
              className={`pb-3 px-3.5 text-xs font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'stream'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>📜</span>
              <span>Master Audit Stream</span>
              <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full font-semibold">
                {filteredLogs.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab('staff')}
              className={`pb-3 px-3.5 text-xs font-bold border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'staff'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <span>👑</span>
              <span>Staff Control</span>
            </button>
          </nav>
        </div>

        {/* ─── TAB 0: Staff Control (Super Admin user management) ─── */}
        {activeTab === 'staff' && <StaffControl />}

        {/* ─── TAB 1: Date-Wise Activity Timeline ─── */}
        {activeTab === 'timeline' && (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Daily Activity Timeline</h2>
                  <p className="text-xs text-slate-500">
                    Day-by-day aggregate of quotations created, PDFs downloaded, and Excels processed
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-semibold text-[11px] uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4 text-center">Quotes Created</th>
                      <th className="py-3 px-4 text-center">PDFs Generated</th>
                      <th className="py-3 px-4 text-center">Excels Exported</th>
                      <th className="py-3 px-4 text-center">Excels Uploaded</th>
                      <th className="py-3 px-4 text-center">Active Staff</th>
                      <th className="py-3 px-4 text-center">Total Operations</th>
                      <th className="py-3 px-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-sans">
                    {filteredDateTimeline.length > 0 ? (
                      filteredDateTimeline.map((d: DateWiseMetricSummary) => {
                        const isToday = d.date === new Date().toISOString().split('T')[0];
                        return (
                          <tr key={d.date} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3 px-4 font-mono font-semibold text-slate-900">
                              <div className="flex items-center gap-2">
                                <span>{d.date}</span>
                                {isToday && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    Today
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center font-mono">
                              <span className="font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-200">
                                {d.quotesCreated}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center font-mono">
                              <span className="font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-lg border border-purple-200">
                                {d.pdfsGenerated}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center font-mono">
                              <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-200">
                                {d.excelsGenerated}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center font-mono">
                              <span className="font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-200">
                                {d.excelsUploaded}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center font-mono font-bold text-cyan-700">
                              {d.activeUsersCount}
                            </td>
                            <td className="py-3 px-4 text-center font-mono font-bold text-slate-900">
                              {d.totalActions}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <button
                                onClick={() => {
                                  setDatePreset('custom');
                                  setCustomStartDate(d.date);
                                  setCustomEndDate(d.date);
                                  setActiveTab('stream');
                                }}
                                className="px-2.5 py-1 text-[11px] bg-slate-100 hover:bg-slate-200 text-emerald-700 font-semibold rounded border border-slate-300 transition-colors cursor-pointer"
                              >
                                View Logs ({d.totalActions}) →
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-400 text-xs">
                          {loading ? 'Aggregating date timeline...' : 'No activity on record for selected date range.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 2: Staff Usage Times & Productivity ─── */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Staff Usage Times & Productivity Matrix</h2>
                  <p className="text-xs text-slate-500">
                    Track total active working duration, session counts, active days, and output per user
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-semibold text-[11px] uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Staff Member</th>
                      <th className="py-3 px-4">Role</th>
                      <th className="py-3 px-4 text-center">Total Usage Time</th>
                      <th className="py-3 px-4 text-center">Active Days</th>
                      <th className="py-3 px-4 text-center">Sessions</th>
                      <th className="py-3 px-4 text-center">Quotes Created</th>
                      <th className="py-3 px-4 text-center">PDFs Exported</th>
                      <th className="py-3 px-4 text-center">Excels (Export/Upload)</th>
                      <th className="py-3 px-4 text-right">Last Active</th>
                      <th className="py-3 px-4 text-center">Filter</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-sans">
                    {metrics?.userMetrics && metrics.userMetrics.length > 0 ? (
                      metrics.userMetrics.map((u) => (
                        <tr key={u.email} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                              <div className="h-8 w-8 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center font-bold text-emerald-700 font-mono text-xs">
                                {u.name ? u.name.slice(0, 2).toUpperCase() : 'SK'}
                              </div>
                              <div>
                                <div className="font-semibold text-slate-900">{u.name}</div>
                                <div className="text-[11px] text-slate-500 font-mono">{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold capitalize bg-slate-100 text-slate-700 border border-slate-200">
                              {u.role.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-mono">
                            <span className="font-bold text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded-lg border border-cyan-200">
                              {formatUsageDuration(u.activeMinutes)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-mono font-bold text-slate-700">
                            {u.activeDaysCount} {u.activeDaysCount === 1 ? 'day' : 'days'}
                          </td>
                          <td className="py-3 px-4 text-center font-mono text-slate-600">
                            {u.sessionCount}
                          </td>
                          <td className="py-3 px-4 text-center font-mono">
                            <span className="font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-200">
                              {u.quotesCreated}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-mono">
                            <span className="font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-lg border border-purple-200">
                              {u.pdfsGenerated}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-mono font-semibold text-emerald-700">
                            {u.excelsGenerated} / {u.excelsUploaded}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-[11px] text-slate-500">
                            {u.lastActiveAt ? new Date(u.lastActiveAt).toLocaleString() : 'Never'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => {
                                setSelectedUserFilter(u.email);
                                setActiveTab('stream');
                              }}
                              className="px-2.5 py-1 text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded border border-slate-300 transition-colors cursor-pointer"
                            >
                              Logs →
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="py-8 text-center text-slate-400 text-xs">
                          {loading ? 'Calculating usage times...' : 'No staff usage times recorded yet.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 3: PDF Generation Ledger & Management ─── */}
        {activeTab === 'pdfs' && (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Quotation PDF Generation Ledger</h2>
                  <p className="text-xs text-slate-500">
                    Audit log of every quotation PDF downloaded, with one-click direct re-download
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-semibold text-[11px] uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Generated At</th>
                      <th className="py-3 px-4">Quotation Ref</th>
                      <th className="py-3 px-4">Sales Rep / User</th>
                      <th className="py-3 px-4">Details</th>
                      <th className="py-3 px-4">Client Telemetry</th>
                      <th className="py-3 px-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-sans">
                    {filteredLogs.filter((l) => l.action_type === 'PDF_GENERATED').length > 0 ? (
                      filteredLogs
                        .filter((l) => l.action_type === 'PDF_GENERATED')
                        .map((l, idx) => (
                          <tr key={l.id || idx} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3 px-4 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                              {new Date(l.created_at).toLocaleString()}
                            </td>
                            <td className="py-3 px-4 font-mono font-bold text-purple-700">
                              {l.resource_id || 'PDF Document'}
                            </td>
                            <td className="py-3 px-4">
                              <div className="font-semibold text-slate-900">{l.user_name || l.email}</div>
                              <div className="text-[11px] text-slate-500 font-mono">{l.email}</div>
                            </td>
                            <td className="py-3 px-4 text-slate-700 max-w-xs truncate">
                              {l.details || '-'}
                            </td>
                            <td className="py-3 px-4 font-mono text-[10px] text-slate-500">
                              <div>IP: {l.ip_address || 'Local'}</div>
                              <div>
                                {l.device_type} · {l.browser}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {l.resource_id && l.resource_id.startsWith('SKI/') ? (
                                <button
                                  onClick={() => handleDownloadQuotePdf(l.resource_id!)}
                                  disabled={generatingDoc === l.resource_id}
                                  className="px-2.5 py-1 text-[11px] bg-purple-50 hover:bg-purple-100 text-purple-700 font-semibold rounded border border-purple-200 transition-colors flex items-center gap-1 mx-auto disabled:opacity-50 cursor-pointer"
                                >
                                  <span>{generatingDoc === l.resource_id ? '⏳' : '📥'}</span>
                                  <span>Download PDF</span>
                                </button>
                              ) : (
                                <span className="text-slate-400 font-mono text-[11px]">Logged</span>
                              )}
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-400 text-xs">
                          No PDF generation events recorded for the selected scope.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 4: Excel Operations Management (Uploads & Downloads) ─── */}
        {activeTab === 'excels' && (
          <div className="space-y-4">
            {/* Subtab Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setExcelSubTab('downloads')}
                className={`px-3 py-1.5 text-xs font-bold rounded-xl border transition-all cursor-pointer ${
                  excelSubTab === 'downloads'
                    ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                    : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900'
                }`}
              >
                📊 Excel Exports & Reports ({rangeCounts.excels})
              </button>
              <button
                onClick={() => setExcelSubTab('uploads')}
                className={`px-3 py-1.5 text-xs font-bold rounded-xl border transition-all cursor-pointer ${
                  excelSubTab === 'uploads'
                    ? 'bg-amber-600 border-amber-600 text-white shadow-sm'
                    : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900'
                }`}
              >
                📤 Excel Bulk Uploads & Imports ({rangeCounts.uploads})
              </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">
                    {excelSubTab === 'downloads'
                      ? 'Excel Generation & Export History'
                      : 'Excel Bulk Master Upload & Import Audits'}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {excelSubTab === 'downloads'
                      ? 'Complete log of quotations, financial statements, and analytics spreadsheets downloaded'
                      : 'Audit log of master spreadsheets uploaded to batch update database tables'}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-semibold text-[11px] uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Date & Time</th>
                      <th className="py-3 px-4">
                        {excelSubTab === 'downloads' ? 'File / Quotation' : 'Uploaded File'}
                      </th>
                      <th className="py-3 px-4">User</th>
                      <th className="py-3 px-4">Details / Outcome</th>
                      <th className="py-3 px-4">Client Telemetry</th>
                      <th className="py-3 px-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-sans">
                    {excelSubTab === 'downloads' ? (
                      filteredLogs.filter(
                        (l) => l.action_type === 'EXCEL_GENERATED' || l.action_type === 'REPORT_EXPORTED'
                      ).length > 0 ? (
                        filteredLogs
                          .filter(
                            (l) => l.action_type === 'EXCEL_GENERATED' || l.action_type === 'REPORT_EXPORTED'
                          )
                          .map((l, idx) => (
                            <tr key={l.id || idx} className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-3 px-4 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                                {new Date(l.created_at).toLocaleString()}
                              </td>
                              <td className="py-3 px-4 font-mono font-bold text-emerald-700">
                                {l.resource_id || 'Excel Spreadsheet'}
                              </td>
                              <td className="py-3 px-4">
                                <div className="font-semibold text-slate-900">{l.user_name || l.email}</div>
                                <div className="text-[11px] text-slate-500 font-mono">{l.email}</div>
                              </td>
                              <td className="py-3 px-4 text-slate-700 max-w-xs truncate">
                                {l.details || '-'}
                              </td>
                              <td className="py-3 px-4 font-mono text-[10px] text-slate-500">
                                <div>IP: {l.ip_address || 'Local'}</div>
                                <div>
                                  {l.device_type} · {l.browser}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {l.resource_id && l.resource_id.startsWith('SKI/') ? (
                                  <button
                                    onClick={() => handleDownloadQuoteExcel(l.resource_id!)}
                                    disabled={generatingDoc === l.resource_id}
                                    className="px-2.5 py-1 text-[11px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold rounded border border-emerald-200 transition-colors flex items-center gap-1 mx-auto disabled:opacity-50 cursor-pointer"
                                  >
                                    <span>{generatingDoc === l.resource_id ? '⏳' : '📥'}</span>
                                    <span>Download .xlsx</span>
                                  </button>
                                ) : (
                                  <span className="text-slate-400 font-mono text-[11px]">Logged</span>
                                )}
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-slate-400 text-xs">
                            No Excel generation events recorded in selected range.
                          </td>
                        </tr>
                      )
                    ) : (
                      filteredLogs.filter((l) => l.action_type === 'EXCEL_UPLOADED').length > 0 ? (
                        filteredLogs
                          .filter((l) => l.action_type === 'EXCEL_UPLOADED')
                          .map((l, idx) => (
                            <tr key={l.id || idx} className="hover:bg-slate-50/80 transition-colors">
                              <td className="py-3 px-4 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                                {new Date(l.created_at).toLocaleString()}
                              </td>
                              <td className="py-3 px-4 font-mono font-bold text-amber-700">
                                {l.resource_id || 'Uploaded Spreadsheet'}
                              </td>
                              <td className="py-3 px-4">
                                <div className="font-semibold text-slate-900">{l.user_name || l.email}</div>
                                <div className="text-[11px] text-slate-500 font-mono">{l.email}</div>
                              </td>
                              <td className="py-3 px-4 text-slate-700 max-w-xs truncate">
                                {l.details || '-'}
                              </td>
                              <td className="py-3 px-4 font-mono text-[10px] text-slate-500">
                                <div>IP: {l.ip_address || 'Local'}</div>
                                <div>
                                  {l.device_type} · {l.browser}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                  Imported
                                </span>
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-slate-400 text-xs">
                            No Excel upload events recorded in selected range.
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 5: Master Live Audit Stream ─── */}
        {activeTab === 'stream' && (
          <div className="space-y-4">
            {/* Filter Bar */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 flex-1">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by quote #, email, description, IP..."
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3.5 py-2 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-2 text-slate-400 hover:text-slate-600 text-xs cursor-pointer"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* User Filter */}
                <select
                  value={selectedUserFilter}
                  onChange={(e) => setSelectedUserFilter(e.target.value)}
                  className="bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-emerald-500"
                >
                  <option value="all">All Staff Members</option>
                  {metrics?.userMetrics.map((u) => (
                    <option key={u.email} value={u.email}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>

                {/* Action Filter */}
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className="bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-700 focus:outline-none focus:border-emerald-500"
                >
                  <option value="all">All Action Types</option>
                  <option value="QUOTATION_CREATED">Quotations Created</option>
                  <option value="PDF_GENERATED">PDFs Generated</option>
                  <option value="EXCEL_GENERATED">Excels Generated</option>
                  <option value="EXCEL_UPLOADED">Excels Uploaded</option>
                  <option value="REPORT_EXPORTED">Reports Exported</option>
                  <option value="USER_LOGIN">User Logins</option>
                  <option value="SESSION_HEARTBEAT">Session Heartbeats</option>
                  <option value="QUOTATION_UPDATED">Quotations Updated</option>
                </select>
              </div>

              {(selectedUserFilter !== 'all' || actionFilter !== 'all' || searchQuery) && (
                <button
                  onClick={() => {
                    setSelectedUserFilter('all');
                    setActionFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-xs text-emerald-600 hover:underline shrink-0 font-medium cursor-pointer"
                >
                  Reset Filters
                </button>
              )}
            </div>

            {/* Logs Table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-semibold text-[11px] uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="py-3 px-4">Timestamp</th>
                      <th className="py-3 px-4">Action</th>
                      <th className="py-3 px-4">Staff Member</th>
                      <th className="py-3 px-4">Resource / Target</th>
                      <th className="py-3 px-4">Details</th>
                      <th className="py-3 px-4">Client Telemetry</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-sans">
                    {filteredLogs.length > 0 ? (
                      filteredLogs.map((log, idx) => {
                        const actionColors: Record<ActivityActionType, string> = {
                          QUOTATION_CREATED: 'bg-blue-50 text-blue-700 border-blue-200',
                          QUOTATION_UPDATED: 'bg-amber-50 text-amber-700 border-amber-200',
                          PDF_GENERATED: 'bg-purple-50 text-purple-700 border-purple-200',
                          EXCEL_GENERATED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                          EXCEL_UPLOADED: 'bg-amber-50 text-amber-700 border-amber-200',
                          REPORT_EXPORTED: 'bg-cyan-50 text-cyan-700 border-cyan-200',
                          USER_LOGIN: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                          SESSION_HEARTBEAT: 'bg-slate-100 text-slate-600 border-slate-200',
                        };

                        const badgeClass =
                          actionColors[log.action_type] ||
                          'bg-slate-100 text-slate-700 border-slate-200';

                        return (
                          <tr key={log.id || idx} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3 px-4 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-bold border ${badgeClass}`}
                              >
                                {log.action_type}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="font-semibold text-slate-900">{log.user_name || log.email}</div>
                              <div className="text-[11px] text-slate-500 font-mono">{log.email}</div>
                            </td>
                            <td className="py-3 px-4 font-mono font-bold text-slate-900">
                              {log.resource_id || '-'}
                            </td>
                            <td className="py-3 px-4 text-slate-700 max-w-xs truncate">
                              {log.details || '-'}
                            </td>
                            <td className="py-3 px-4 font-mono text-[10px] text-slate-500">
                              <div>IP: {log.ip_address || 'Local'}</div>
                              <div>
                                {log.device_type} · {log.browser}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-10 text-center text-slate-400 text-xs">
                          {loading
                            ? 'Fetching activity stream...'
                            : 'No activity records match the selected filters.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
